import { useEffect, useState } from "react";
import { Check, ExternalLink, Flag, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";

const reportDate = (value) => value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Recently";
const reportCategory = (value) => ({
  spam: "Spam or misleading content",
  harassment: "Harassment or bullying",
  hate_speech: "Hate speech or discrimination",
  sexual_content: "Sexual content",
  violence: "Violence or threats",
  misinformation: "Misinformation",
  other: "Something else",
}[value] || value);

export const ChannelCommentReports = ({ onOpenAccount, onChanged }) => {
  const [reports, setReports] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [reviewMessages, setReviewMessages] = useState({});
  const [error, setError] = useState("");

  const load = () => api.get("/channel/customer/admin/comment-reports")
    .then(({ data }) => {
      setReports(Array.isArray(data) ? data : []);
      setError("");
    })
    .catch((cause) => setError(errText(cause)));

  useEffect(() => { load(); }, []);

  const resolve = async (report, decision) => {
    setBusyId(report.id);
    try {
      await api.post(`/channel/customer/admin/comment-reports/${report.id}/resolve`, {
        decision,
        message: reviewMessages[report.id] || "",
      });
      await load();
      onChanged?.();
      toast.success(decision === "dismissed" ? "Report dismissed and reporter notified" : "Outcome sent to reporter");
    } catch (cause) { toast.error(errText(cause)); }
    finally { setBusyId(""); }
  };

  const deleteReport = async (report) => {
    if (!window.confirm("Delete this report? The comment will remain unchanged.")) return;
    setBusyId(report.id);
    try {
      await api.delete(`/channel/customer/admin/comment-reports/${report.id}`);
      await load();
      onChanged?.();
      toast.success("Report deleted");
    } catch (cause) { toast.error(errText(cause)); }
    finally { setBusyId(""); }
  };

  const removeComment = async (report) => {
    if (!window.confirm("Delete this comment? The author's account and its report history will remain.")) return;
    const message = window.prompt("Optional message to include with the comment removal notification:", "");
    if (message === null) return;
    setBusyId(report.id);
    try {
      await api.delete(`/channel/customer/admin/comments/${report.comment_id}`, { data: { message } });
      await api.post(`/channel/customer/admin/comment-reports/${report.id}/resolve`, {
        decision: "action_taken",
        message: reviewMessages[report.id] || "The comment was removed.",
      });
      await load();
      onChanged?.();
      toast.success("Comment deleted and report actioned");
    } catch (cause) { toast.error(errText(cause)); }
    finally { setBusyId(""); }
  };

  return (
    <section className="space-y-5" data-testid="channel-comment-reports">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-black/15 pb-4">
        <div><p className="font-mono-label text-xs uppercase tracking-[0.2em] text-[#E62314]">Moderation</p><h2 className="mt-2 font-display text-2xl font-extrabold">Comment reports</h2><p className="mt-1 text-sm text-[#57534E]">Reports link to the comment author’s account. Account actions remain your decision.</p></div>
        <button type="button" onClick={load} aria-label="Refresh comment reports" className="border border-black/15 bg-white p-2.5 hover:border-[#E62314]"><RefreshCw className="h-4 w-4" /></button>
      </header>
      {error && <p className="border border-[#E62314]/30 bg-white p-4 text-sm text-[#A3160C]" role="alert">{error}</p>}
      {!error && !reports.length && <p className="border border-black/10 bg-white p-5 text-sm text-[#57534E]">No comment reports yet.</p>}
      <ul className="space-y-3">
        {reports.map((report) => <li key={report.id} className="space-y-4 border border-black/15 bg-white p-4 sm:p-5" data-testid={`channel-comment-report-${report.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-xs font-bold uppercase text-[#A3160C]"><Flag className="h-3.5 w-3.5" />{report.status}</p>
              <p className="mt-2 text-sm text-[#57534E]">Reported {reportDate(report.created_at)} by <span className="font-semibold text-black">{report.reporter_name || "Channel account"}</span>{report.reporter_username && <span> · @{report.reporter_username}</span>}</p>
              <p className="mt-2 text-sm font-bold">Comment by</p>
              <button type="button" onClick={() => onOpenAccount?.(report.author_id)} className="mt-1 inline-flex items-center gap-1 text-left text-sm font-semibold text-[#A3160C] underline underline-offset-2 hover:text-black" data-testid={`channel-comment-report-author-${report.id}`}>
                {report.author_name || "Unknown account"}{report.author_username && ` · @${report.author_username}`}<ExternalLink className="h-3.5 w-3.5" />
              </button>
              {report.author_pending_reports > 1 && <p className="mt-1 text-xs font-semibold text-[#A3160C]">{report.author_pending_reports} pending reports for this account</p>}
            </div>
            {report.status === "pending" && <span className="border border-amber-700/20 bg-amber-50 px-2.5 py-1 text-xs font-bold uppercase text-amber-800">Pending</span>}
          </div>
          <blockquote className="whitespace-pre-wrap border-l-2 border-black/15 bg-[#F7F6F3] px-4 py-3 text-sm leading-6 text-[#292725]">{report.comment_text || "Comment text unavailable"}</blockquote>
          {report.category && <p className="text-sm"><strong>Category:</strong> {reportCategory(report.category)}</p>}
          {report.reason && <p className="text-sm"><strong>Reason:</strong> {report.reason}</p>}
          {report.review_message && <p className="text-sm"><strong>Outcome sent:</strong> {report.review_message}</p>}
          {report.status === "pending" ? <div className="flex flex-wrap gap-2">
            <label className="w-full text-sm font-semibold">Outcome message for reporter <span className="font-normal text-[#57534E]">(optional)</span><textarea maxLength={1000} rows={2} value={reviewMessages[report.id] || ""} onChange={(event) => setReviewMessages((current) => ({ ...current, [report.id]: event.target.value }))} placeholder="Add context about your decision." className="mt-2 block w-full border border-black/20 px-3 py-2.5 text-sm font-normal" data-testid={`channel-comment-report-outcome-${report.id}`} /></label>
            <button type="button" disabled={busyId === report.id} onClick={() => removeComment(report)} className="inline-flex items-center gap-2 bg-[#A3160C] px-3 py-2 text-xs font-bold text-white hover:bg-[#7D120A] disabled:opacity-50" data-testid={`channel-comment-report-delete-${report.id}`}><Trash2 className="h-3.5 w-3.5" />Delete comment</button>
            <button type="button" disabled={busyId === report.id} onClick={() => resolve(report, "action_taken")} className="inline-flex items-center gap-2 border border-black/20 px-3 py-2 text-xs font-bold hover:border-black disabled:opacity-50"><Check className="h-3.5 w-3.5" />Action taken</button>
            <button type="button" disabled={busyId === report.id} onClick={() => resolve(report, "dismissed")} className="inline-flex items-center gap-2 border border-black/20 px-3 py-2 text-xs font-bold hover:border-black disabled:opacity-50"><Check className="h-3.5 w-3.5" />No action taken</button>
          </div> : <p className="text-xs text-[#57534E]">Reviewed {report.reviewed_at ? reportDate(report.reviewed_at) : ""}</p>}
          <button type="button" disabled={busyId === report.id} onClick={() => deleteReport(report)} className="inline-flex items-center gap-2 border border-black/20 px-3 py-2 text-xs font-bold hover:border-[#A3160C] hover:text-[#A3160C] disabled:opacity-50" data-testid={`channel-comment-report-remove-${report.id}`}><Trash2 className="h-3.5 w-3.5" />Delete report</button>
        </li>)}
      </ul>
    </section>
  );
};